// Phase NA1-8 — NAATI Author/Reviewer byline (E-E-A-T mirror of NotaryAuthorByline)
import { NAATI_TRANSLATORS } from "@/data/naati-translators";
import { BUILD_DATE } from "@/lib/aeo";
import { Languages, ShieldCheck } from "lucide-react";

export default function NaatiAuthorByline({ lang = "th" }: { lang?: "th" | "en" }) {
  const isTH = lang === "th";
  const lead = NAATI_TRANSLATORS[0];
  const reviewer = NAATI_TRANSLATORS[1] ?? NAATI_TRANSLATORS[0];

  const L = isTH
    ? {
        translatedBy: "แปลและตรวจสอบโดย",
        reviewedBy: "ตรวจสอบคุณภาพโดย",
        cpn: "NAATI CPN",
        level: "ระดับ",
        recert: "ผ่านการ Recertification ปี",
        validity: "อายุใบรับรองถึง",
        updated: "อัปเดตล่าสุด",
        verifiedAt: "ตรวจสอบโดย NAATI Certified Translator ขึ้นทะเบียนจริง",
      }
    : {
        translatedBy: "Translated and verified by",
        reviewedBy: "Quality-reviewed by",
        cpn: "NAATI CPN",
        level: "Level",
        recert: "Recertified",
        validity: "Valid until",
        updated: "Last updated",
        verifiedAt: "Verified by NAATI Certified Translators on active register",
      };

  return (
    <aside
      className="mx-auto my-8 max-w-6xl rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-amber-50/40 p-5 px-4 sm:px-6"
      aria-label={L.translatedBy}
      data-track-zone="naati-byline"
    >
      <div className="flex items-start gap-2 text-emerald-700">
        <ShieldCheck className="mt-0.5 h-4 w-4" />
        <span className="text-[10px] font-bold uppercase tracking-widest">{L.verifiedAt}</span>
      </div>
      <dl className="mt-3 grid gap-3 text-[13px] sm:grid-cols-2">
        <div itemScope itemType="https://schema.org/Person">
          <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <Languages className="mr-1 inline-block h-3 w-3" /> {L.translatedBy}
          </dt>
          <dd className="mt-1">
            <span itemProp="name" className="font-bold text-navy-deep">
              {isTH ? lead.nameTh : lead.nameEn}
            </span>
            <span className="ml-2 text-[11px] text-slate-500">· {lead.level}</span>
            <div className="mt-0.5 text-[11px] text-slate-600">
              {L.cpn}: <span itemProp="identifier">{lead.cpn}</span> · {L.recert} {lead.recertifiedYear} · {L.validity} {lead.expiresYear}
            </div>
            <meta itemProp="jobTitle" content={lead.level} />
            <meta itemProp="knowsLanguage" content="th,en" />
            <meta itemProp="worksFor" content="NYC Language Institute" />
          </dd>
        </div>
        <div itemScope itemType="https://schema.org/Person">
          <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <ShieldCheck className="mr-1 inline-block h-3 w-3" /> {L.reviewedBy}
          </dt>
          <dd className="mt-1">
            <span itemProp="name" className="font-bold text-navy-deep">
              {isTH ? reviewer.nameTh : reviewer.nameEn}
            </span>
            <span className="ml-2 text-[11px] text-slate-500">· {reviewer.level}</span>
            <div className="mt-0.5 text-[11px] text-slate-600">
              {L.cpn}: <span itemProp="identifier">{reviewer.cpn}</span> · {L.validity} {reviewer.expiresYear}
            </div>
          </dd>
        </div>
      </dl>
      <p className="mt-3 text-[11px] text-slate-500">
        {L.updated}: <time dateTime={BUILD_DATE}>{BUILD_DATE}</time>
      </p>
    </aside>
  );
}
